<template>
  <v-container fluid class="operator-page fill-height">
    <v-card class="operator-card mx-auto" elevation="8">
      <v-card-title class="operator-header">
        <div>
          <div class="text-h5 font-weight-bold">Selecciona tu usuario</div>
          <div class="text-body-2 mt-1">{{ businessName }}</div>
        </div>
        <v-btn text color="primary" @click="logoutAccount">Cambiar cuenta</v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>
        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-3">Cargando usuarios...</div>
        </div>
        <v-row v-else>
          <v-col
            v-for="operator in operators"
            :key="operator.id"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card
              outlined
              class="operator-item text-center pa-5"
              tabindex="0"
              @click="selectOperator(operator)"
              @keyup.enter="selectOperator(operator)"
            >
              <v-avatar color="primary" size="64" class="mb-3">
                <span class="white--text text-h6">{{ initials(operator.name) }}</span>
              </v-avatar>
              <div class="text-h6">{{ operator.name }}</div>
              <v-icon v-if="operator.hasPassword" small color="grey" class="mt-2">mdi-lock</v-icon>
            </v-card>
          </v-col>
        </v-row>
        <v-alert v-if="!loading && !operators.length && !error" type="info" dense>
          No hay usuarios activos disponibles.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-dialog v-model="pinDialog" max-width="380" persistent>
      <v-card>
        <v-card-title>
          <div>
            <div>{{ selectedOperator ? selectedOperator.name : '' }}</div>
            <div class="text-caption font-weight-regular">Ingresa tu PIN para continuar</div>
          </div>
          <v-spacer />
          <v-btn icon :disabled="authenticating" @click="closePinDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="pin"
            type="password"
            readonly
            outlined
            label="PIN"
            :error-messages="pinError"
            append-icon="mdi-backspace-outline"
            @click:append="removeLastDigit"
            @keyup.enter="submitOperator"
          />
          <div class="pin-grid">
            <v-btn v-for="number in numbers" :key="number" large @click="appendDigit(number)">
              {{ number }}
            </v-btn>
            <v-btn large @click="pin = ''">C</v-btn>
            <v-btn large @click="appendDigit('0')">0</v-btn>
            <v-btn color="primary" large :loading="authenticating" @click="submitOperator">
              Entrar
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  clearOperatorSession,
  listOperators,
  loginOperator,
} from '@/services/posOperatorService';

const AUTH_KEYS = ['token', 'userRole', 'userId', 'userName', 'businessId', 'businessName', 'mustChangePassword'];

export default {
  name: 'OperatorSelectView',
  data() {
    return {
      operators: [],
      loading: false,
      authenticating: false,
      error: '',
      pinError: '',
      pinDialog: false,
      selectedOperator: null,
      pin: '',
      numbers: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    };
  },
  computed: {
    businessName() {
      return localStorage.getItem('businessName') || 'Tu negocio';
    },
  },
  created() {
    clearOperatorSession();
    this.loadOperators();
  },
  methods: {
    async loadOperators() {
      this.loading = true;
      this.error = '';
      try {
        this.operators = await listOperators();
      } catch (error) {
        this.error = error.response?.data?.mensaje || 'No fue posible cargar los usuarios.';
      } finally {
        this.loading = false;
      }
    },
    initials(name) {
      return String(name || 'U')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('');
    },
    async selectOperator(operator) {
      this.selectedOperator = operator;
      this.pin = '';
      this.pinError = '';
      if (!operator.hasPassword) {
        await this.submitOperator();
        return;
      }
      this.pinDialog = true;
    },
    closePinDialog() {
      this.pinDialog = false;
      this.selectedOperator = null;
      this.pin = '';
      this.pinError = '';
    },
    appendDigit(value) {
      if (this.pin.length < 64) this.pin += value;
    },
    removeLastDigit() {
      this.pin = this.pin.slice(0, -1);
    },
    async submitOperator() {
      if (!this.selectedOperator || this.authenticating) return;
      this.authenticating = true;
      this.pinError = '';
      try {
        await loginOperator(this.selectedOperator.id, this.pin);
        this.pinDialog = false;
        await this.$router.replace('/dashboard');
      } catch (error) {
        this.pin = '';
        this.pinDialog = true;
        this.pinError = error.response?.data?.mensaje || error.message || 'Usuario o PIN incorrecto.';
      } finally {
        this.authenticating = false;
      }
    },
    logoutAccount() {
      clearOperatorSession();
      AUTH_KEYS.forEach(key => localStorage.removeItem(key));
      this.$router.replace('/login');
    },
  },
};
</script>

<style scoped>
.operator-page {
  min-height: 100vh;
  background: #f2f4f7;
  padding: 32px;
}
.operator-card {
  width: min(960px, 100%);
  border-radius: 16px;
}
.operator-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 24px;
}
.operator-item {
  cursor: pointer;
  min-height: 170px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.operator-item:hover,
.operator-item:focus {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);
  outline: none;
}
.pin-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
@media (max-width: 600px) {
  .operator-page { padding: 12px; }
  .operator-header { align-items: flex-start; }
}
</style>
